import type { FC, PropsWithChildren } from "react";
import React, { useMemo, useRef } from "react";

import cn from "classnames";

import { getChildrenRecord } from "../../../utils/react";
import { ContentsView, Content } from "../../Content";
import { useResizeDirection } from "../hooks/useResizeDirection";

import type {
    PropsWithCustomVarsStyles,
    PropsWithTsId,
} from "../../../types/react";

const TOAST_TEST_ID = "toast-test-id";

export interface IToastCommonProps {
    /** Заголовок тоста */
    title?: string;
    /** Подзаголовок тоста */
    subtitle?: string;
    /** Отвечает за направление тоста. Автоматически меняется с horizontal на vertical, если количество строк в заголовке или подзаголовке начинает превышать или уже превышает 2 при достижении максимальной ширины контейнера */
    direction?: "horizontal" | "vertical";
    /** Отвечает за значение ресайзинга ширины компонента */
    fillHorizontal?: boolean;
    /** Отвечает за отображение анимации таймера */
    timer?: boolean;
    /** Пресет тоста (разные цветовые токены) */
    preset?: "positive" | "attention" | "negative" | "info";
    /** Классы для стилей */
    classes: Record<string, string>;
    /** Иконка по умолчанию (если хотим ее поменять в ToastAlert) */
    defaultIcon?: React.ReactNode;
    /** Длительность анимации таймера (если меняем дефолтное значение duration у таймера). Значение задается в ms */
    timerDuration?: number;
}

export type ToastCommonProps = PropsWithCustomVarsStyles<IToastCommonProps> &
    Partial<PropsWithTsId>;

export const ToastCommon: FC<PropsWithChildren<ToastCommonProps>> = (props) => {
    const {
        title,
        subtitle,
        direction = "horizontal",
        timer = false,
        fillHorizontal = false,
        preset,
        "data-tsid": tsid,
        customVarsStyles,
        children,
        classes,
        defaultIcon,
        timerDuration,
    } = props;

    const { ToastIcon, ButtonsView } = useMemo(
        () =>
            getChildrenRecord(children, "Toast", {
                ToastIcon: {
                    props: {
                        size: "24",
                    },
                },
            }),
        [children],
    );

    const wrapperClassName = cn(
        classes["wrapper"],
        preset && classes[`__color-${preset}`],
        fillHorizontal && classes["__fill-horizontal"],
        customVarsStyles,
    );

    const titleRef = useRef<HTMLDivElement>(null);
    const subtitleRef = useRef<HTMLDivElement>(null);

    const currentDirection = useResizeDirection(
        titleRef,
        subtitleRef,
        direction,
    );

    const directionToastClassName = cn(
        classes["content-container"],
        classes[`__position-${currentDirection}`],
    );

    const buttonsContainerClassName = cn(
        classes["buttons-container"],
        currentDirection === "vertical" && !ToastIcon && classes["__no-icon"],
    );

    const duration = useMemo(() => {
        if (timerDuration !== undefined) {
            return `${timerDuration}ms`;
        }
        return ButtonsView ? "8000ms" : "4000ms";
    }, [timerDuration, ButtonsView]);

    const TIMER_HEIGHT = {
        HORIZONTAL: "105%",
        VERTICAL: "103%",
    } as const;

    return (
        <div
            className={wrapperClassName}
            role="alert"
            data-tsid={tsid || TOAST_TEST_ID}
        >
            {timer && (
                <>
                    <div
                        className={classes["main-timer"]}
                        style={{
                            ["--timer-height" as any]:
                                currentDirection === "horizontal"
                                    ? TIMER_HEIGHT.HORIZONTAL
                                    : TIMER_HEIGHT.VERTICAL,
                        }}
                    >
                        <div
                            className={classes["timer"]}
                            style={{
                                ["--duration" as any]: duration,
                                ["--timer-height" as any]:
                                    currentDirection === "horizontal"
                                        ? TIMER_HEIGHT.HORIZONTAL
                                        : TIMER_HEIGHT.VERTICAL,
                            }}
                        ></div>
                        <div
                            className={classes["timer-glow"]}
                            style={{
                                ["--duration" as any]: duration,
                            }}
                        />
                    </div>
                    <div className={classes["pulse-container"]}>
                        <div className={classes["pulse"]}></div>
                    </div>
                </>
            )}

            <div className={directionToastClassName}>
                <div className={classes["content-wrapper"]}>
                    {ToastIcon ? (
                        <div className={classes["icon-container"]}>
                            {ToastIcon}
                        </div>
                    ) : (
                        defaultIcon && (
                            <div className={classes["icon-container"]}>
                                {defaultIcon}
                            </div>
                        )
                    )}

                    <div className={classes["text-content"]}>
                        <ContentsView>
                            <ContentsView.Row>
                                <ContentsView.Content>
                                    <Content.Text
                                        view="custom"
                                        customColorsStyles={classes["text"]}
                                        maxLines={2}
                                        ref={titleRef}
                                    >
                                        {title}
                                    </Content.Text>
                                </ContentsView.Content>
                            </ContentsView.Row>
                            <ContentsView.Row>
                                <ContentsView.Content>
                                    <Content.Text
                                        view="custom"
                                        customColorsStyles={
                                            classes["description"]
                                        }
                                        maxLines={3}
                                        ref={subtitleRef}
                                    >
                                        {subtitle}
                                    </Content.Text>
                                </ContentsView.Content>
                            </ContentsView.Row>
                        </ContentsView>
                    </div>
                </div>

                {ButtonsView && (
                    <div className={buttonsContainerClassName}>
                        {ButtonsView}
                    </div>
                )}
            </div>
        </div>
    );
};
